import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import * as yup from 'yup'
import * as R from 'ramda'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import accent from 'accents'
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Container,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { LoadingOverlay } from 'common/components'

import MaterialTable from 'material-table'
import AddCircleIcon from '@material-ui/icons/AddCircle'

import { Header } from '../layout'
import { requestSpecialtiesGet, requestSpecialtyCreate } from 'common/actions'
import {
  getError,
  getLoading,
  getSpecialties,
  getSuccess,
} from 'common/reducers/specialties'
import theme from 'common/theme'

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  inputBox: {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  fullWidth: {
    width: '100%',
  },
}))

const Specialties = ({
  error,
  loading,
  specialties,
  onRequestSpecialties,
  onCreateSpecialty,
  success,
}) => {
  const classes = useStyles()
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [newSpecialtyModal, setNewSpecialtyModal] = useState(false)
  const [selectedSpecialty, setSelectedSpecialty] = useState(null)

  const schema = yup.object().shape({
    name: yup
      .string()
      .required('Este campo es requerido')
      .test('existing-specialty', 'La especialidad ya existe', (value) => {
        const exists = R.any(
          (s) => R.equals(R.toLower(accent(s.name)), R.toLower(accent(value))),
          specialties
        )
        return !exists
      }),
  })

  const { control, errors, handleSubmit, triggerValidation } = useForm({
    defaultValues: {
      name: '',
    },
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    onRequestSpecialties(true)
  }, [])

  const onSubmit = (data) => {
    setNewSpecialtyModal(false)
    onCreateSpecialty(data)
  }

  return (
    <>
      {loading && <LoadingOverlay />}
      <Header />
      <Container className={classes.container}>
        <MaterialTable
          columns={[{ title: 'Nombre', field: 'name', editable: 'never' }]}
          data={specialties || []}
          title='Especialidades'
          options={{}}
          actions={[
            {
              icon: () => <AddCircleIcon color='primary' />,
              tooltip: 'Nueva Especialidad',
              position: 'toolbar',
              onClick: () => {
                setNewSpecialtyModal(true)
              },
            },
          ]}
          localization={{
            pagination: {
              labelDisplayedRows: '{from}-{to} de {count}',
              labelRowsSelect: 'filas',
              labelRowsPerPage: 'Filas por página: ',
            },
            toolbar: {
              searchPlaceholder: 'Búsqueda',
              searchTooltip: 'Búsqueda',
            },
            header: {
              actions: 'Acciones',
            },
            body: {
              emptyDataSourceMessage: 'No hay especialidades',
              filterRow: {
                filterTooltip: 'Filtrar',
              },
              editTooltip: 'Editar',
            },
          }}
        />
        <Dialog
          open={newSpecialtyModal}
          onClose={() => setNewSpecialtyModal(false)}
        >
          <DialogTitle id='alert-dialog-title'>
            Crear nueva especialidad
          </DialogTitle>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <Controller
                control={control}
                name='name'
                error={!!errors.name}
                helperText={errors.name && errors.name.message}
                label='Nombre'
                onBlur={() => triggerValidation('name')}
                fullWidth
                as={<TextField />}
              />
              {error && (
                <Box mt={3}>
                  <Alert severity='error'>
                    No se puede eliminar la especialidad, ya hay datos
                    relacionados con la misma
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                color='primary'
                onClick={() => setNewSpecialtyModal(false)}
              >
                Volver
              </Button>
              <Button type='submit' color='primary' variant='contained'>
                CREAR
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog
          open={deleteConfirmationModal}
          onClose={() => {
            setDeleteConfirmationModal(false)
            setSelectedSpecialty(null)
          }}
        >
          {selectedSpecialty && (
            <>
              <DialogTitle id='alert-dialog-title'>
                Eliminar {selectedSpecialty.name}
              </DialogTitle>
              <DialogContent>
                {selectedSpecialty && (
                  <DialogContentText id='alert-dialog-description'>
                    ¿Está seguro que desea eliminar la especialidad{' '}
                    {selectedSpecialty.name}?
                  </DialogContentText>
                )}
                {selectedSpecialty.providerCount && (
                  <Box mt={3}>
                    <Alert severity='error'>
                      No se puede eliminar la especialidad, ya hay datos
                      relacionados con la misma
                    </Alert>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  color='primary'
                  variant='contained'
                  onClick={() => {
                    setDeleteConfirmationModal(false)
                    setSelectedSpecialty(null)
                  }}
                >
                  Volver
                </Button>
                <Button
                  style={theme.danger}
                  disabled={!!selectedSpecialty.providerCount}
                >
                  Si, eliminar
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    error: getError(state),
    loading: getLoading(state),
    specialties: getSpecialties(state),
    success: getSuccess(state),
  }
}

export default connect(mapStateToProps, {
  onRequestSpecialties: requestSpecialtiesGet,
  onCreateSpecialty: requestSpecialtyCreate,
})(Specialties)
